import React from 'react';
import {
  Button,
  Checkbox,
  Field,
  Input,
  Select,
  SelectItem,
  SelectTrigger,
  Spinner,
} from '@billda/design-system';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useInviteUser } from '../hooks/useInviteUser';
import { useAssignableRoles } from '../hooks/useAssignableRoles';
import {
  inviteUserSchema,
  InviteUserFormData,
} from '../types/inviteUserSchema';
import { useSnackbarStore } from '../../../shared/stores/snackbarStore';
import { ADMIN_ROLE_NAME } from '../utils/roleChangePolicy';

interface InviteUserPanelProps {
  onClose?: () => void;
  onInviteSent?: () => void;
}

const InviteUserPanel: React.FC<InviteUserPanelProps> = ({
  onClose,
  onInviteSent,
}) => {
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);
  const {
    data: roles = [],
    isLoading: rolesLoading,
    assignableRoles,
  } = useAssignableRoles();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm<InviteUserFormData>({
    defaultValues: {
      email: '',
      role: null,
      isBookingWork: true,
    },
    resolver: yupResolver(inviteUserSchema),
    mode: 'onChange',
  });

  // Watch role to conditionally show checkbox
  const selectedRole = watch('role');
  const selectedRoleName = roles.find(r => r.id === selectedRole)?.name;
  const isAdminRole = selectedRoleName === ADMIN_ROLE_NAME;

  const { mutate: inviteUser, isPending: isInviting } = useInviteUser();

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const onSubmit = (data: InviteUserFormData) => {
    inviteUser(data, {
      onSuccess: () => {
        showSnackbar('Invitation sent successfully', 'success');
        reset();
        onInviteSent?.();
      },
    });
  };

  return (
    <div className="p-6">
      <p className="type-body-md mb-6 text-content-secondary">
        Send an invitation to a new user to join your organisation.
      </p>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-6">
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <Field label="Email Address" error={errors.email?.message}>
                <Input
                  {...field}
                  type="email"
                  disabled={isInviting}
                  autoComplete="email"
                  data-testid="invite-email-input"
                />
              </Field>
            )}
          />

          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <Field label="Role" error={errors.role?.message}>
                <Select
                  value={field.value != null ? String(field.value) : undefined}
                  onValueChange={value => {
                    field.onChange(value === '' ? null : Number(value));
                  }}
                  disabled={isInviting || rolesLoading}
                  data-testid="invite-role-select"
                >
                  <SelectTrigger />
                  {rolesLoading ? (
                    <SelectItem value="loading" disabled>
                      <span className="flex items-center gap-2">
                        <Spinner size={20} />
                        Loading roles...
                      </span>
                    </SelectItem>
                  ) : (
                    assignableRoles.map(role => (
                      <SelectItem
                        key={role.id}
                        value={String(role.id)}
                        description={role.description ?? undefined}
                      >
                        {role.name}
                      </SelectItem>
                    ))
                  )}
                </Select>
              </Field>
            )}
          />

          {isAdminRole && (
            <Controller
              name="isBookingWork"
              control={control}
              render={({ field }) => (
                <label className="flex items-center gap-2 type-body-md">
                  <Checkbox
                    checked={!field.value}
                    onCheckedChange={checked => {
                      field.onChange(!checked);
                    }}
                    data-testid="invite-not-booking-work-checkbox"
                  />
                  This user will not be booking work
                </label>
              )}
            />
          )}
        </div>

        <div className="mt-8 flex gap-4">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={isInviting}
            fullWidth
          >
            Cancel
          </Button>
          <Button
            variant="neutral"
            type="submit"
            disabled={!isValid || isInviting || rolesLoading}
            loading={isInviting}
            fullWidth
            data-testid="invite-submit-button"
          >
            {isInviting ? 'Sending...' : 'Send Invitation'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default InviteUserPanel;
